import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import { useNavigate } from "react-router-dom";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import DirectoryRowCard from "../components/DirectoryRowCard";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
} from "../components/saasStyles";

const orderStatuses = [
  "order_open",
  "order_sent_to_odoo",
  "in_preparation",
  "ready_for_delivery",
  "pmo_in_charge",
  "technician_assigned",
  "appointment_proposed",
  "appointment_confirmed",
  "installation_done",
  "closed",
  "cancelled",
];

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [quickAction, setQuickAction] = useState("default");
  const [editForm, setEditForm] = useState({ status: "order_open", notes: "", statusNote: "" });

  const loadData = async () => {
    const [ordersResponse, leadsResponse] = await Promise.all([api.get("/api/orders"), api.get("/api/leads")]);
    setOrders(ordersResponse.data);
    setLeads(leadsResponse.data);
  };

  const loadOrderDetail = async (id) => {
    const response = await api.get(`/api/orders/${id}`);
    setSelectedOrder(response.data);
    setEditForm({
      status: response.data.status,
      notes: response.data.notes || "",
      statusNote: "",
    });
  };

  useEffect(() => {
    loadData().catch(() => setMessage({ type: "error", text: "Les commandes n'ont pas pu etre chargees." }));
  }, []);

  useEffect(() => {
    if (!selectedOrderId) {
      setSelectedOrder(null);
      return;
    }
    loadOrderDetail(selectedOrderId).catch(() => setMessage({ type: "error", text: "Les details de la commande n'ont pas pu etre charges." }));
  }, [selectedOrderId]);

  const filteredOrders = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? orders
      : orders.filter((order) =>
          [order.contactName, order.whatsappNumber, order.status, ...order.items.map((item) => item.productName)]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle)
        );

    if (quickAction === "ready") return base.filter((order) => order.status === "ready_for_delivery");
    if (quickAction === "open") return base.filter((order) => order.status === "order_open");
    if (quickAction === "closed") return base.filter((order) => order.status === "closed");
    return base;
  }, [orders, search, quickAction]);

  const convertibleLeads = leads.filter((lead) => lead.status !== "lead_won");

  const handleConvertLead = async () => {
    try {
      await api.post("/api/orders/from-lead", { leadId: Number(selectedLeadId) });
      setSelectedLeadId("");
      await loadData();
      setMessage({ type: "success", text: "Le prospect a ete converti en commande." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le prospect n'a pas pu etre converti en commande." });
    }
  };

  const handleSaveOrder = async () => {
    if (!selectedOrderId) return;

    try {
      await api.patch(`/api/orders/${selectedOrderId}`, editForm);
      await loadData();
      await loadOrderDetail(selectedOrderId);
      setMessage({ type: "success", text: "La commande a ete mise a jour." });
    } catch (_error) {
      setMessage({ type: "error", text: "La commande n'a pas pu etre mise a jour." });
    }
  };

  const handleSendToOdoo = async () => {
    if (!selectedOrderId) return;

    try {
      const response = await api.post(`/api/orders/${selectedOrderId}/send-to-odoo`);
      await loadData();
      await loadOrderDetail(selectedOrderId);
      setMessage({
        type: "success",
        text: response.data?.remoteId
          ? `La commande a ete envoyee vers Odoo (${response.data.remoteId}).`
          : "La commande a ete envoyee vers Odoo.",
      });
    } catch (error) {
      const code = error?.response?.data?.error;
      if (code === "odoo_not_configured") {
        setMessage({ type: "error", text: "La connexion REST Odoo n'est pas encore configuree." });
        return;
      }
      if (code === "odoo_request_timeout") {
        setMessage({ type: "error", text: "Odoo ne repond pas dans les delais." });
        return;
      }
      if (code === "odoo_request_rejected") {
        setMessage({ type: "error", text: "Odoo a refuse l'export de cette commande." });
        return;
      }
      setMessage({ type: "error", text: "La commande n'a pas pu etre envoyee vers Odoo." });
    }
  };

  const renderHistoryNote = (entry) => {
    if (!entry.technicianId || !entry.technicianName || !entry.note?.includes(entry.technicianName)) {
      return entry.note || "Aucune note";
    }

    const [before, after] = entry.note.split(entry.technicianName);

    return (
      <>
        {before}
        <Box
          component="button"
          type="button"
          onClick={() => navigate(`/technicians?technicianId=${entry.technicianId}`)}
          sx={{
            display: "inline",
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: "#25324b",
            fontWeight: 800,
            cursor: "pointer",
            textDecoration: "underline",
            fontSize: "inherit",
            fontFamily: "inherit",
          }}
        >
          {entry.technicianName}
        </Box>
        {after}
      </>
    );
  };

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Repertoire des commandes"
      description="Vue claire des commandes, des changements de statut et des actions operationnelles."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={() => setCreateOpen(true)} sx={saasHeroActionButtonSx}>
          + Nouvelle commande
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box sx={{ ...saasDirectoryWrapSx, borderRadius: "44px", px: { xs: 2, md: 2.5 }, py: { xs: 1.2, md: 1.4 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>Afficher {filteredOrders.length} elements</Typography>
        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher une commande"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ ...saasInputSx, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "18px", minHeight: 44, backgroundColor: "#fff", boxShadow: "none" } }}
            InputProps={{ startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} /> }}
          />
          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select value={quickAction} onChange={(event) => setQuickAction(event.target.value)} displayEmpty sx={{ minHeight: 56, borderRadius: "18px", bgcolor: "#fff", fontWeight: 700, boxShadow: "none" }}>
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="open">Commandes ouvertes</MenuItem>
              <MenuItem value="ready">Pretes a livrer</MenuItem>
              <MenuItem value="closed">Commandes cloturees</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ ...saasSoftCountSx, minWidth: 96, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>{filteredOrders.length} commandes</Typography>
          </Box>
          <Box sx={{ width: 58, height: 44, borderRadius: "16px", border: "1px solid #dfe6f4", bgcolor: "#fff", display: "grid", placeItems: "center" }}>
            <Badge badgeContent={orders.filter((order) => order.status === "ready_for_delivery").length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}><NotificationsRoundedIcon /></IconButton>
            </Badge>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.9fr" }, gap: 3 }}>
        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Commandes</Typography>
              <Typography variant="body2" color="text.secondary">Vue structuree avec selection rapide, contact client et contexte commande.</Typography>
            </Box>
            <Stack direction="row" spacing={1.25}>
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Visibles</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{filteredOrders.length}</Typography></Box></Box>
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Selection</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{selectedOrder ? 1 : 0}</Typography></Box></Box>
            </Stack>
          </Box>

          {filteredOrders.map((order) => (
            <DirectoryRowCard
              key={order.id}
              selected={selectedOrderId === order.id}
              avatarText={`#${order.id}`}
              title={order.contactName || "Client inconnu"}
              subtitle={`OD-${order.id}`}
              chips={[
                { label: order.status === "ready_for_delivery" ? "Pret" : order.status, color: order.status === "ready_for_delivery" ? "success" : "default" },
                { label: order.items[0]?.categoryName || "Sans categorie", color: "default" },
              ]}
              columns={[
                { label: "Contact", value: order.whatsappNumber || "-", secondary: order.locationText || "Aucune localisation" },
                { label: "Produits", value: order.items.map((item) => item.productName).join(", ") || "-", secondary: `Prospect ${order.leadId || "-"}` },
              ]}
              actionLabel={selectedOrderId === order.id ? "Selectionne" : "Choisir"}
              onSelect={() => setSelectedOrderId(order.id)}
            />
          ))}
        </Box>

        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 }, alignContent: "start" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Espace commande</Typography>
            <Typography variant="body2" color="text.secondary">Gestion du statut, des notes et de l'historique.</Typography>
          </Box>

          {!selectedOrder ? (
            <Typography variant="body2" color="text.secondary">Choisissez une commande a gauche pour la modifier.</Typography>
          ) : (
            <>
              <Box sx={{ ...saasSoftCountSx, minWidth: 120 }}>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>Prospect</Typography>
                <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>{selectedOrder.leadId || "-"}</Typography>
              </Box>
              <Box sx={{ ...saasSoftCountSx, minWidth: 120 }}>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>Odoo</Typography>
                <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
                  {selectedOrder.odooId || "Pas encore envoye"}
                </Typography>
              </Box>
              {selectedOrder.sentToOdooAt ? (
                <Typography variant="caption" color="text.secondary">
                  Dernier envoi Odoo: {new Date(selectedOrder.sentToOdooAt).toLocaleString("fr-FR")}
                </Typography>
              ) : null}
              <TextField select label="Statut" value={editForm.status} onChange={(event) => setEditForm((c) => ({ ...c, status: event.target.value }))} sx={saasInputSx}>
                {orderStatuses.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </TextField>
              <TextField label="Note" multiline minRows={4} value={editForm.notes} onChange={(event) => setEditForm((c) => ({ ...c, notes: event.target.value }))} sx={saasInputSx} />
              <TextField label="Note de statut" multiline minRows={2} value={editForm.statusNote} onChange={(event) => setEditForm((c) => ({ ...c, statusNote: event.target.value }))} sx={saasInputSx} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <Button variant="contained" onClick={handleSaveOrder} sx={{ borderRadius: "14px" }}>
                  Enregistrer la commande
                </Button>
                <Button variant="outlined" onClick={handleSendToOdoo} sx={{ borderRadius: "14px" }}>
                  Envoyer vers Odoo
                </Button>
              </Stack>

              <Box sx={{ display: "grid", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>Historique</Typography>
                {(selectedOrder.history || []).map((entry) => (
                  <Box key={entry.id} sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.25, bgcolor: "#fdfefe" }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#25324b" }}>{entry.oldStatus || "debut"} -> {entry.newStatus}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      {renderHistoryNote(entry)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Creer une commande depuis un prospect</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1.5 }}>
          <TextField select label="Choisir un prospect" value={selectedLeadId} onChange={(event) => setSelectedLeadId(event.target.value)} sx={saasInputSx}>
            {convertibleLeads.map((lead) => (
              <MenuItem key={lead.id} value={lead.id}>{lead.contactName} - {lead.items.map((item) => item.productName).join(", ")}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={async () => { await handleConvertLead(); setCreateOpen(false); }} disabled={!selectedLeadId}>Creer la commande</Button>
        </DialogActions>
      </Dialog>
    </PageFrame>
  );
}
